import { useState } from "react";
import { TeamInfo } from "@/api/teamCompare";
import { getPointsBadge, getTeamLogo } from "@/lib/utils";

type PlayerType = "players" | "goalkeepers";

interface CombinedPlayersTableProps {
    chosenInfo: TeamInfo;
    opponentInfo: TeamInfo;
}

export const CombinedPlayersTable = ({ chosenInfo, opponentInfo }: CombinedPlayersTableProps) => {
    const [playerType, setPlayerType] = useState<PlayerType>("players");

    const chosenLogo = getTeamLogo(chosenInfo.teamName);
    const opponentLogo = getTeamLogo(opponentInfo.teamName);

    const rows = [
        ...(playerType === "players" ? chosenInfo.playersKpi : chosenInfo.goalkeepersKpi).map((p) => ({
            ...p,
            teamName: chosenInfo.teamName,
            logo: chosenLogo,
        })),
        ...(playerType === "players" ? opponentInfo.playersKpi : opponentInfo.goalkeepersKpi).map((p) => ({
            ...p,
            teamName: opponentInfo.teamName,
            logo: opponentLogo,
        })),
    ].sort((a, b) => b.kpi - a.kpi);

    return (
        <div className="flex flex-col">
            <div className="flex flex-col gap-2 mb-3 shrink-0 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-lg font-headline font-bold section-title">
                    {playerType === "players" ? "Players" : "Goalkeepers"} Comparison
                </h3>
                <nav className="rounded-md p-1 bg-surface-container-low sm:hidden">
                    <div className="grid grid-cols-2 gap-1">
                        {(["players", "goalkeepers"] as const).map((type) => (
                            <button
                                key={type}
                                onClick={() => setPlayerType(type)}
                                className={`nav-tab ${playerType === type ? "nav-tab-active" : "nav-tab-inactive"}`}
                            >
                                {type}
                            </button>
                        ))}
                    </div>
                </nav>
                <nav className="hidden sm:flex rounded-md p-0.5 gap-0.5 data-table-wrapper">
                    {(["players", "goalkeepers"] as const).map((type) => (
                        <button
                            key={type}
                            onClick={() => setPlayerType(type)}
                            className={`px-3 py-1 text-[10px] font-headline font-black uppercase tracking-widest rounded transition-all duration-150 cursor-pointer ${
                                playerType === type
                                    ? "bg-white text-primary shadow-sm"
                                    : "text-on-surface-variant hover:text-on-surface"
                            }`}
                        >
                            {type}
                        </button>
                    ))}
                </nav>
            </div>
            <div className="overflow-auto rounded-xl data-table-wrapper">
                {rows.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-on-surface-variant text-sm text-center px-4">
                        No players to display
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="data-table-header sticky top-0 z-10">
                                <th className="px-3 sm:px-4 py-3 font-headline font-black text-[10px] uppercase tracking-widest text-center data-table-label-cell w-12">
                                    Team
                                </th>
                                <th className="px-3 sm:px-6 py-3 font-headline font-black text-[10px] uppercase tracking-widest text-left">
                                    Player
                                </th>
                                <th className="px-3 sm:px-6 py-3 font-headline font-black text-[10px] uppercase tracking-widest text-right">
                                    KPI
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const { bg, text, border } = getPointsBadge(row.kpi);
                                return (
                                    <tr key={`${row.teamName}-${row.playerName}`} className="data-table-row-striped">
                                        <td className="px-3 sm:px-4 py-3.5 data-table-label-cell text-center">
                                            {row.logo ? (
                                                <img
                                                    src={row.logo}
                                                    alt={row.teamName}
                                                    className="w-6 h-6 object-contain inline-block"
                                                    title={row.teamName}
                                                />
                                            ) : (
                                                <span className="text-[10px] font-bold text-on-surface-variant">
                                                    {row.teamName.slice(0, 3).toUpperCase()}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 sm:px-6 py-3.5 text-sm font-medium text-on-surface">
                                            {row.playerName}
                                        </td>
                                        <td className="px-3 sm:px-6 py-3.5 text-right">
                                            <div
                                                className={`inline-flex items-center px-2.5 py-1 ${bg} ${text} rounded font-bold text-sm border ${border}`}
                                            >
                                                {row.kpi.toFixed(1)}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};
